import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { isMobile } from '../utils/responsive';
import { shouldShowResolutionScan } from '../utils/queryResolutionDisplay';

/**
 * QueryResolutionDisplay - Animated component showing how AI interpreted a user's query
 * 
 * Features:
 * - Horizontal "AI scan" wipe animation transforming original → resolved query
 * - Glowing scan line with cyan/purple gradient
 * - Entity tags showing detected player, team, market, etc.
 * - Hover to reveal original query
 * - Compact variant for background queries list
 * - Supports new _queryResolution structured metadata from backend
 * 
 * @param {string} originalQuery - The user's raw input
 * @param {object} responseMetadata - API response with detection info
 * @param {string} resolvedQuery - Optional explicit resolved query
 * @param {boolean} compact - Smaller variant for lists
 * @param {boolean} autoAnimate - Start animation automatically (default true)
 * @param {number} animationDelay - Delay before starting animation (ms)
 */

const QueryResolutionDisplay = ({ 
  originalQuery = '',
  responseMetadata = {},
  resolvedQuery: explicitResolved,
  compact = false,
  autoAnimate = true,
  animationDelay = 500,
  style
}) => {
  // Animation states
  const [isHovering, setIsHovering] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showEntityTags, setShowEntityTags] = useState(false);
  const scanLineProgress = useRef(new Animated.Value(0)).current;
  const glowPulse = useRef(new Animated.Value(0)).current;
  const textOpacity = useRef(new Animated.Value(1)).current;
  const entityTagsOpacity = useRef(new Animated.Value(0)).current;
  
  // ═══════════════════════════════════════════════════════════
  // EXTRACT QUERY RESOLUTION DATA
  // Priority: _queryResolution > _resolvedQuery > fallback build
  // ═══════════════════════════════════════════════════════════
  const queryResolution = responseMetadata?._queryResolution || null;
  
  const resolvedQuery = useMemo(() => {
    // Priority 1: Explicit prop
    if (explicitResolved) return explicitResolved;
    
    // Priority 2: Backend's structured _queryResolution object
    if (queryResolution?.resolved && queryResolution.resolved !== queryResolution.original) {
      return queryResolution.resolved;
    }
    
    // Priority 3: Backend's pre-built resolved query
    if (responseMetadata?._resolvedQuery) {
      return responseMetadata._resolvedQuery;
    }
    
    // Priority 4: Build from individual metadata fields (legacy fallback)
    const {
      _queryClassification,
      _detectedPlayer,
      _detectedTeam,
      _homeTeam,
      _awayTeam,
      _league,
      _market,
      _preferredSportsbook,
      _lineThreshold,
      _lineDirection,
      _matchup,
      detectedMarketInfo,
      results
    } = responseMetadata || {};
    
    const player = _detectedPlayer?.name || _detectedPlayer;
    const team = _detectedTeam?.name || _detectedTeam;
    const league = _league || _queryClassification?.league || results?.[0]?.query?.league;
    const market = _market || detectedMarketInfo?.market || _queryClassification?.market || results?.[0]?.query?.market;
    const homeTeam = _homeTeam || _matchup?.team2 || results?.[0]?.fixture?.home_team;
    const awayTeam = _awayTeam || _matchup?.team1 || results?.[0]?.fixture?.away_team;
    const sportsbook = _preferredSportsbook || _queryClassification?.sportsbook;
    const lineValue = _lineThreshold || detectedMarketInfo?.lineThreshold;
    const lineDir = _lineDirection || detectedMarketInfo?.lineDirection;
    
    const parts = [];
    
    if (player) {
      parts.push(player);
      if (lineDir && lineValue) parts.push(`${lineDir} ${lineValue}`);
      if (market) {
        const cleanMarket = market.replace(/^player_/i, '').replace(/_/g, ' ')
          .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        parts.push(cleanMarket);
      }
    } else if (homeTeam && awayTeam) {
      parts.push(`${awayTeam} vs ${homeTeam}`);
      if (market && market.toLowerCase() !== 'all') parts.push(`- ${market}`);
    } else if (team) {
      parts.push(team);
      if (market) parts.push(market);
    } else if (league) {
      parts.push(league.toUpperCase());
      parts.push(sportsbook ? `on ${sportsbook}` : 'Best Odds');
    }
    
    if (parts.length === 0) return null;
    return parts.join(' ');
  }, [explicitResolved, responseMetadata, queryResolution]);
  
  // ═══════════════════════════════════════════════════════════
  // BUILD ENTITY TAGS from _queryResolution.entities
  // ═══════════════════════════════════════════════════════════
  const entityTags = useMemo(() => {
    const entities = queryResolution?.entities || {};
    const tags = [];
    
    if (entities.player) tags.push({ label: entities.player, type: 'player', icon: '🏅' });
    if (entities.team) tags.push({ label: entities.team, type: 'team', icon: '🏟️' });
    if (entities.market) {
      const cleanMarket = entities.market.replace(/^player_/i, '').replace(/_/g, ' ')
        .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      tags.push({ label: cleanMarket, type: 'market', icon: '📊' });
    }
    if (entities.line && entities.direction) {
      tags.push({ label: `${entities.direction} ${entities.line}`, type: 'line', icon: '📏' });
    }
    if (entities.sportsbook) tags.push({ label: entities.sportsbook, type: 'sportsbook', icon: '🎰' });
    if (entities.league) tags.push({ label: entities.league.toUpperCase(), type: 'league', icon: '🏆' });
    
    return tags;
  }, [queryResolution]);
  
  // Method & confidence badge
  const methodBadge = useMemo(() => {
    if (!queryResolution) return null;
    const conf = queryResolution.confidence || 0;
    const method = queryResolution.method || 'unknown';
    
    const methodLabel = {
      engine_primary: 'Precision Match',
      smart_engine: 'Smart Match',
      direct_match: 'Direct Match',
      llm_fallback: 'AI Resolved',
    }[method] || 'Matched';
    
    const confLabel = conf >= 0.9 ? 'High' : conf >= 0.7 ? 'Medium' : 'Low';
    const confColor = conf >= 0.9 ? '#4ade80' : conf >= 0.7 ? '#fbbf24' : '#f87171';
    
    return { label: methodLabel, confidence: confLabel, color: confColor };
  }, [queryResolution]);
  
  // Check if we have a meaningful transformation
  const hasTransformation = resolvedQuery && 
    resolvedQuery.toLowerCase().trim() !== originalQuery.toLowerCase().trim();
  
  const playScan = shouldShowResolutionScan({ autoAnimate, hasTransformation });

  // Fresh query or a loading→done flip must not keep a half-played scan.
  useEffect(() => {
    setAnimationComplete(false);
    setShowEntityTags(false);
    setIsHovering(false);
    scanLineProgress.setValue(0);
    glowPulse.setValue(0);
    entityTagsOpacity.setValue(0);
    textOpacity.setValue(1);
  }, [originalQuery, playScan]);

  // ═══════════════════════════════════════════════════════════
  // ANIMATION SEQUENCE
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!playScan) return;
    
    const timeout = setTimeout(() => {
      // Start glow pulse
      Animated.loop(
        Animated.sequence([
          Animated.timing(glowPulse, {
            toValue: 1,
            duration: 600,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          }),
          Animated.timing(glowPulse, {
            toValue: 0.4,
            duration: 600,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          })
        ])
      ).start();
      
      // Main scan animation
      Animated.timing(scanLineProgress, {
        toValue: 1,
        duration: 1200,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
        useNativeDriver: false
      }).start(() => {
        setAnimationComplete(true);
        // Stop glow
        Animated.timing(glowPulse, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true
        }).start();
        
        // Show entity tags with stagger
        if (entityTags.length > 0) {
          setShowEntityTags(true);
          Animated.timing(entityTagsOpacity, {
            toValue: 1,
            duration: 400,
            delay: 200,
            easing: Easing.out(Easing.ease),
            useNativeDriver: true,
          }).start();
        }
      });
    }, animationDelay);
    
    return () => clearTimeout(timeout);
  }, [playScan, animationDelay, entityTags.length]);
  
  // Handle hover
  const handleHoverIn = () => {
    if (animationComplete) {
      setIsHovering(true);
      Animated.timing(textOpacity, {
        toValue: 0.7,
        duration: 150,
        useNativeDriver: true
      }).start();
    }
  };
  
  const handleHoverOut = () => {
    setIsHovering(false);
    Animated.timing(textOpacity, {
      toValue: 1,
      duration: 150,
      useNativeDriver: true
    }).start();
  };
  
  // Loading (or no rewrite): one string only — never the stacked scan pair.
  if (!playScan) {
    return (
      <View style={[styles.container, compact && styles.containerCompact, style]}>
        <Text style={[styles.queryText, compact && styles.queryTextCompact]}>
          "{originalQuery}"
        </Text>
      </View>
    );
  }
  
  // Interpolations
  const scanLineLeft = scanLineProgress.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%']
  });
  
  const resolvedOpacity = scanLineProgress.interpolate({
    inputRange: [0, 0.3, 1],
    outputRange: [0, 0.5, 1]
  });
  
  const originalOpacity = scanLineProgress.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0.3, 0]
  });
  
  const glowOpacity = glowPulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 1]
  });
  
  const displayText = isHovering ? originalQuery : resolvedQuery;
  
  return (
    <View 
      style={[styles.container, compact && styles.containerCompact, style]}
      {...(Platform.OS === 'web' ? {
        onMouseEnter: handleHoverIn,
        onMouseLeave: handleHoverOut
      } : {})}
    >
      {/* Top row: badge + method indicator */}
      {animationComplete && (
        <View style={styles.topRow}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>✨ AI Enhanced</Text>
          </View>
          {methodBadge && !compact && (
            <View style={styles.methodBadge}>
              <View style={[styles.confidenceDot, { backgroundColor: methodBadge.color }]} />
              <Text style={styles.methodText}>{methodBadge.label}</Text>
            </View>
          )}
        </View>
      )}
      
      {/* Main query display */}
      <View style={styles.queryContainer}>
        {/* Invisible sizer so stacked lines don't collapse to one row */}
        <Text
          style={[styles.queryText, compact && styles.queryTextCompact, styles.sizerText]}
          pointerEvents="none"
        >
          "{(resolvedQuery && resolvedQuery.length > originalQuery.length) ? resolvedQuery : originalQuery}"
        </Text>
        {/* Original query (fades out) */}
        {!animationComplete && (
          <Animated.Text 
            style={[
              styles.queryText, 
              compact && styles.queryTextCompact,
              styles.overlayText,
              { opacity: originalOpacity }
            ]}
          >
            "{originalQuery}"
          </Animated.Text>
        )}
        
        {/* Resolved query (fades in) */}
        <Animated.Text 
          style={[
            styles.queryText, 
            compact && styles.queryTextCompact,
            styles.overlayText,
            animationComplete ? { opacity: textOpacity } : { opacity: resolvedOpacity },
            animationComplete && isHovering && styles.hoveringText
          ]}
        >
          "{displayText}"
        </Animated.Text>
        
        {/* Scan line with glow */}
        {!animationComplete && (
          <Animated.View 
            style={[
              styles.scanLineContainer,
              { left: scanLineLeft }
            ]}
          >
            <Animated.View style={[styles.scanGlow, { opacity: glowOpacity }]}>
              <LinearGradient
                colors={['transparent', 'rgba(0, 217, 255, 0.4)', 'rgba(168, 85, 247, 0.4)', 'transparent']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.scanGlowGradient}
              />
            </Animated.View>
            <LinearGradient
              colors={['#00D9FF', '#A855F7']}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={styles.scanLine}
            />
          </Animated.View>
        )}
      </View>
      
      {/* Entity tags row */}
      {showEntityTags && entityTags.length > 0 && !compact && (
        <Animated.View style={[styles.entityTagsRow, { opacity: entityTagsOpacity }]}>
          {entityTags.map((tag, i) => (
            <View key={`${tag.type}-${i}`} style={[styles.entityTag, styles[`entityTag_${tag.type}`]]}>
              <Text style={styles.entityTagIcon}>{tag.icon}</Text>
              <Text style={styles.entityTagLabel}>{tag.label}</Text>
            </View>
          ))}
        </Animated.View>
      )}
      
      {/* Hover hint */}
      {animationComplete && !compact && (
        <Text style={styles.hoverHint}>
          {isHovering ? 'Your original search' : 'Hover to see original'}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.25)',
    overflow: 'hidden',
  },
  containerCompact: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  badge: {
    backgroundColor: 'rgba(0, 217, 255, 0.15)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0, 217, 255, 0.3)',
  },
  badgeText: {
    color: '#00D9FF',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  methodBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  confidenceDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 5,
  },
  methodText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 10,
    fontWeight: '500',
  },
  queryContainer: {
    position: 'relative',
    minHeight: 24,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  queryText: {
    color: '#c4b5fd',
    fontSize: isMobile ? 14 : 15,
    fontWeight: '500',
    fontStyle: 'italic',
    lineHeight: 22,
  },
  queryTextCompact: {
    fontSize: 13,
    lineHeight: 18,
  },
  sizerText: {
    opacity: 0,
  },
  overlayText: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  hoveringText: {
    color: 'rgba(196, 181, 253, 0.7)',
  },
  // Entity tags
  entityTagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  entityTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  entityTag_player: {
    borderColor: 'rgba(74, 222, 128, 0.3)',
    backgroundColor: 'rgba(74, 222, 128, 0.08)',
  },
  entityTag_team: {
    borderColor: 'rgba(96, 165, 250, 0.3)',
    backgroundColor: 'rgba(96, 165, 250, 0.08)',
  },
  entityTag_market: {
    borderColor: 'rgba(251, 191, 36, 0.3)',
    backgroundColor: 'rgba(251, 191, 36, 0.08)',
  },
  entityTag_line: {
    borderColor: 'rgba(168, 85, 247, 0.3)',
    backgroundColor: 'rgba(168, 85, 247, 0.08)',
  },
  entityTag_sportsbook: {
    borderColor: 'rgba(244, 114, 182, 0.3)',
    backgroundColor: 'rgba(244, 114, 182, 0.08)',
  },
  entityTag_league: {
    borderColor: 'rgba(0, 217, 255, 0.3)',
    backgroundColor: 'rgba(0, 217, 255, 0.08)',
  },
  entityTagIcon: {
    fontSize: isMobile ? 10 : 11,
    marginRight: 4,
  },
  entityTagLabel: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: isMobile ? 11 : 12,
    fontWeight: '500',
  },
  // Scan animation
  scanLineContainer: {
    position: 'absolute',
    top: -4,
    bottom: -4,
    width: 40,
    zIndex: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scanLine: {
    width: 2,
    height: '100%',
    borderRadius: 1,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 0 12px 4px rgba(0, 217, 255, 0.6), 0 0 24px 8px rgba(168, 85, 247, 0.4)',
    } : {
      shadowColor: '#00D9FF',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.8,
      shadowRadius: 12,
    }),
  },
  scanGlow: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: -30,
    right: -30,
  },
  scanGlowGradient: {
    flex: 1,
  },
  hoverHint: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 10,
    marginTop: 8,
    textAlign: 'center',
    fontWeight: '500',
  },
});

export default QueryResolutionDisplay;
